import { datadogRum } from '@datadog/browser-rum'
import { Component } from 'react'
import {
  AttributeMappings,
  ConfigurationDetail,
  DatadogEncodingRun,
  DatadogEncodingRunDeviceManager,
  DatadogEncodingRunKilledTag,
  DatadogEncodingRunReplaceTag,
  EncodingCounters,
  EncodingProductionOrderRows,
  Encodings,
  EncodingValidationRequest,
  EncodingValidationResponse,
  IdentifierType,
  ProductionOrderRow,
  TagPasswordIdentifiers,
  TagPasswordResponse,
  TmrSimpleAttributesMap,
  TmrTag,
  TrmEncodingVerifyGroupedTag,
  TrmEncodingVerifyReplaceTag,
  TrmEncodingVerifyTag,
  WorkOrderRows,
  WorkOrderRow
} from 'stylewhere/api'
import {
  Box,
  Button,
  EncodingCountersBox,
  EncodingReading,
  EncodingRightHeader,
  FullLoadingLayer,
  IdentifierBox,
  IdentifierListModal,
  InfoBox,
  ItemAttributesCard,
  Page,
  ProductInfoBox,
  Spacer
} from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { AppStore, OperationReadingProps, RemoteOperation, RfidReader, Router } from 'stylewhere/shared'
import { __, T } from 'stylewhere/shared/i18n'
import { EncodingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import { getNfcReadConfiguration, getNfcTagReadErrors, isNfcAuthEnabled } from 'stylewhere/shared/nfcAuthentication'
import {
  askUserConfirmation,
  canKillToAssociateItem,
  enabledDisassociateItemTag,
  getAutomaticRestartAntenna,
  getDesiredCode,
  getEnableUhfWrite,
  getEncodingPageLayout,
  getEncodingProductFields,
  getEncodingTagPassword,
  getGroupedKillTags,
  getInitialType,
  getReverseUid,
  getShowItemAttributesBox,
  getStartAntennaOrder,
  getTimeoutValidateTags,
  getUseTidTargetDuringWrite,
  isExpectOnlyMandatoryIdentifiers,
  isModalError,
  isProductionEnvironment,
  getScanIdentifierConfig,
  isValidOperationEncodingTagType,
  MAX_KILL_TAG_ATTEMPT,
  MAX_WRITE_ATTEMPT,
  showCountersOnEncodingVerify,
  showToast,
  showToastError,
  sleep,
  uuid,
  isPreEncodingWorkOrder,
  getLockTagOnWriteFailure,
  getCheckTagAfterWrite
} from 'stylewhere/shared/utils'
import { OperationEncodingTagType } from 'stylewhere/types'

interface State {
  encodingValidation: EncodingValidationRequest
  encodingValidationResponse?: EncodingValidationResponse
  antennaOn: boolean
  processing: boolean | 'nfcDecryption'
  options: {
    value: string
    label: string
    active: boolean
  }[]
  starting: boolean
  originTemplatePath?: string
  targetTags: TrmEncodingVerifyTag[]
  targetTag: TrmEncodingVerifyTag[]
  progress: boolean
  tagGroupSelected?: TrmEncodingVerifyGroupedTag
  visibleModalTags: boolean
  numKillAttempt: number
  numWriteAttempt: number
  writeRetryCount: number
  currentTagType: OperationEncodingTagType
  startedTagType: OperationEncodingTagType
  replaceTag: boolean
  productionOrderRow?: ProductionOrderRow
  workOrderRow?: WorkOrderRow
  counters?: EncodingCounters
  replaceTagLoading: boolean
  itemAttributes?: TmrSimpleAttributesMap[]
}

const KILL_CUSTOM_COMMANDS_OPERATION_TYPE = 'LOW_LEVEL_REQ'

export default class EncodingVerify extends Component<OperationReadingProps<State>, State> {
  get matchParams() {
    return Router.getMatchParams(this.props)
  }
  get locationState() {
    return Router.getLocationState<State>(this.props)
  }
  get operation() {
    return RemoteOperation.getOperationConfig<EncodingOperationConfig>(this.matchParams.opCode)
  }
  timer: NodeJS.Timeout | null = null
  isModal = false
  isValidate = false
  isDecodeNfc = false
  get tagTypesToCheck() {
    return getStartAntennaOrder(this.operation)
  }
  countTags = 0
  numKillAttempt = 0
  inReplacing = false
  get initialType() {
    return getInitialType(this.operation)
  }
  datadogSession: DatadogEncodingRun | undefined = undefined
  currentKillPassword = 0
  killPasswords = []
  mutualAuth = false
  restartAntennaAfterDisassociate = true
  writeTagStartTime?: Date

  state: State = {
    encodingValidation: {
      operationId: this.operation.id,
      identifiers: []
    },
    options: [
      ...(this.operation.options.showOnlyVerify === 'true'
        ? []
        : [{ value: 'associate', label: __(T.misc.associate), active: false }]),
      { value: 'verify', label: __(T.misc.verify_association), active: true }
    ],
    antennaOn: false,
    processing: false,
    starting: false,
    targetTags: [],
    targetTag: [],
    progress: false,
    visibleModalTags: false,
    numKillAttempt: 0,
    numWriteAttempt: 0,
    writeRetryCount: 0,
    currentTagType: this.tagTypesToCheck[0],
    startedTagType: 'ALL',
    tagGroupSelected: undefined,
    replaceTag: false,
    counters: undefined,
    replaceTagLoading: false
  }

  componentDidMount() {
    this.isModal = isModalError(this.operation)
    this.setState({ originTemplatePath: this.locationState.originTemplatePath }, this.startReader)
  }

  getEncodingKillTagMode = () => {
    return this.operation.options.encodingKillTagMode ?? 'uhf_nfc'
  }

  getAntennaCustomCommands = () => {
    return EncodingExtensions.getAntennaCustomCommandsOperationType(this.operation)
  }

  startReader = async () => {
    const { currentTagType, starting } = this.state
    if (starting) {
      return
    }
    this.setState({ starting: true }, async () => {
      try {
        const scanIdentifierConfig = getScanIdentifierConfig(this.operation)

        await RfidReader.initialize(this.getAntennaCustomCommands())
        RfidReader.setOnTagReadCallback(this.onTagRead)
        RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
        RfidReader.setAutomaticStopTime(this.operation.autostopAntennaTimeout)
        RfidReader.setNfcReadConfiguration(getNfcReadConfiguration(this.operation))
        RfidReader.onStartCallback = this.onStartCallback
        RfidReader.onStopCallback = this.onStopCallback

        if (AppStore.getEmulation()) {
          this.setState({ starting: false })
        } else if (scanIdentifierConfig.rfid) {
          await RfidReader.start({
            restart: getAutomaticRestartAntenna(this.operation),
            tagTypeToCheck: currentTagType,
            customCommandsOperationType: this.getAntennaCustomCommands()
          })
        }
        this.initializeDataDogSession()
      } catch (error) {
        showToastError(
          (error as any).message ?? __(T.error.rfid_reader_initialization),
          __(T.error.error),
          this.isModal
        )
      }
    })
  }

  initializeDataDogSession = () => {
    const isActive = AppStore.isDatadogActive()
    if (isActive) {
      this.datadogSession = {
        runId: uuid().toString(),
        operationCode: this.operation.code,
        startTime: new Date(),
        errors: []
      }
    }
  }

  cleanDataDogSessionDeviceManager = () => {
    if (this.datadogSession?.deviceManager) {
      delete this.datadogSession.deviceManager
    }
  }

  trackReadEpcMismatch = (requestedEpc: string, readEpc: string | undefined) => {
    if (!readEpc || readEpc === requestedEpc) return
    if (!this.datadogSession || AppStore.getEmulation()) return
    this.cleanDataDogSessionDeviceManager()
    datadogRum.addAction('readEpcMismatch', {
      ...this.datadogSession,
      additionalInfo: {
        ...this.datadogSession.additionalInfo,
        requestedEpc,
        readEpc
      }
    })
  }

  onStartCallback = () => {
    const { currentTagType } = this.state
    this.setState({ antennaOn: true, starting: false, startedTagType: currentTagType })
  }

  onStopCallback = (automatic = false) => {
    const { currentTagType, startedTagType, encodingValidation } = this.state
    if (currentTagType === 'ALL' || AppStore.getEmulation()) {
      this.setState({ antennaOn: false, starting: false })
    } else {
      const tmpEncoding = encodingValidation
      // setta il tag del tipo che non siamo riusciti a leggere in errore
      const find = tmpEncoding.identifiers.find(
        (id) =>
          (id.identifierType === 'UHF_TAG' && currentTagType === 'UHF') ||
          (id.identifierType === 'NFC_TAG' && currentTagType === 'NFC')
      )

      const errorText = __(T.imbustatrice.dynamic_tag_missing, { role: currentTagType })
      if (find && !find._read && ['TO_BE_READ', 'EXPECTED'].includes(find._status)) {
        find._error = errorText
        find._status = 'ERROR'
      } else if (!find) {
        tmpEncoding.identifiers.push({
          identifierType: currentTagType === 'UHF' ? 'UHF_TAG' : currentTagType === 'NFC' ? 'NFC_TAG' : 'UHF_TAG',
          _status: 'ERROR',
          _added: true,
          _error: errorText,
          role: currentTagType
        })
      }

      if (automatic) {
        const nextTagType = this.getNextTagType()

        this.setState(
          {
            antennaOn: false,
            starting: false,
            encodingValidation: tmpEncoding,
            currentTagType: nextTagType || currentTagType,
            startedTagType: nextTagType ? 'ALL' : startedTagType
          },
          async () => {
            if (nextTagType) {
              await this.startAntenna(nextTagType)
            }
          }
        )
      } else {
        this.setState({
          antennaOn: false,
          starting: false,
          encodingValidation: tmpEncoding
        })
      }
    }
  }

  componentWillUnmount = () => {
    this.datadogSession = undefined
    RfidReader.onStartCallback = undefined
    RfidReader.onStopCallback = undefined
    this.isDecodeNfc = false
    this.setState({ processing: false })
    RfidReader.onStartCallback = undefined
    RfidReader.onStopCallback = undefined
    void this.stopAntenna()
  }

  getProcessingMessage = () => {
    return __(this.state.processing === 'nfcDecryption' ? T.messages.nfcDecryption : T.messages.operation_in_progress)
  }

  getNextTagType = () => {
    const { currentTagType } = this.state

    const currentTagTypeIndex = this.tagTypesToCheck.indexOf(this.state.currentTagType)
    let nextTagType: OperationEncodingTagType
    if (currentTagType !== 'ALL' && currentTagTypeIndex + 1 < this.tagTypesToCheck.length) {
      nextTagType = this.tagTypesToCheck[currentTagTypeIndex + 1]
    }
    return nextTagType
  }

  removeTagReadFromReader = (tags: TmrTag[]) => {
    const toRemove: string[] = []
    for (const element of tags) {
      toRemove.push(element.uid ?? element.epc)
    }
    RfidReader.removeTags(toRemove)
  }

  getTagTypeReceived = (tag: TmrTag): OperationEncodingTagType | undefined => {
    if (tag.uid) return 'NFC'
    if (tag.epc) return 'UHF'
    return undefined
  }

  finishTagRead = async (
    encodingValidation: EncodingValidationRequest,
    currentTagType: OperationEncodingTagType,
    nextTagType?: OperationEncodingTagType
  ) => {
    if (nextTagType) {
      await RfidReader.stop({ manual: true, customCommandsOperationType: this.getAntennaCustomCommands() })
      this.setState(
        { encodingValidation, currentTagType: nextTagType, startedTagType: 'ALL', starting: true },
        async () => {
          await sleep(100)
          await this.startAntenna(nextTagType)
          this.onTagReadTimer()
        }
      )
      return
    }
    if (currentTagType !== 'ALL') {
      await RfidReader.stop({ manual: true, customCommandsOperationType: this.getAntennaCustomCommands() })
    }
    this.setState({ encodingValidation }, this.onTagReadTimer)
  }

  /** Returns true if the read should be aborted (NFC auth error or collision). */
  handleNfcAuthFailure = (encodingValidation: EncodingValidationRequest): boolean => {
    const { nfcError, nfcCollision } = getNfcTagReadErrors(encodingValidation)
    if (!nfcError && !nfcCollision) {
      this.setState({ processing: false })
      return false
    }
    this.setState({ processing: false }, async () => {
      this.isValidate = false
      await RfidReader.stop({ manual: true, customCommandsOperationType: this.getAntennaCustomCommands() })
    })
    if (nfcCollision && this.datadogSession) {
      this.datadogSession.errors?.push({ error: 'nfcCollision' })
      datadogRum.addAction('err_nfcCollision', this.datadogSession)
    }
    return true
  }

  onTagRead = async (tag: TmrTag, antennaId?: string) => {
    //added a sleep to destroy correctly the timers or wait isValidate flag
    await sleep(Math.random() * 500)

    const { encodingValidation, currentTagType } = this.state

    // se la lettura sequenziale antenne è attiva e ricevo un tipo di tag non aspettato lo ignora
    const tagTypeReceived = this.getTagTypeReceived(tag)
    if (currentTagType !== 'ALL' && tagTypeReceived !== currentTagType) {
      this.removeTagReadFromReader([tag])
      return
    }

    if (this.isValidate) {
      this.removeTagReadFromReader([tag])
      return
    }
    if (this.isDecodeNfc) {
      this.removeTagReadFromReader([tag])
      return
    }
    // if time to validate is 0 then set isEncoding to true immediately for not have validated in parallel
    const timeoutValidateTags = getTimeoutValidateTags(this.operation)
    if (timeoutValidateTags !== undefined && timeoutValidateTags === 0) {
      this.isValidate = true
    }
    try {
      const nextTagType = this.getNextTagType()
      this.stopTimer()

      // no pre-selected item/config exists yet on this page (unlike EncodingEncode), so unlike there
      // we can't gate on encodingValidationResponse.configuration.details - it's only populated after
      // the first verifyTag() call, i.e. after the first tag has already been read.
      const nfcNeedsAuthentication = !!tag.uid && isNfcAuthEnabled(this.operation)
      if (nfcNeedsAuthentication) {
        this.isDecodeNfc = true
        this.setState({ processing: 'nfcDecryption' })
      }

      await EncodingExtensions.onTagRead(encodingValidation, tag, this.operation, antennaId, this.datadogSession)

      if (nfcNeedsAuthentication && this.handleNfcAuthFailure(encodingValidation)) {
        return
      }

      await this.finishTagRead(encodingValidation, currentTagType, nextTagType)
    } catch (error) {
      console.error(error)
    } finally {
      this.isDecodeNfc = false
    }
  }

  onTagReadTimer = async () => {
    const timeoutValidateTags = getTimeoutValidateTags(this.operation)
    if (timeoutValidateTags !== undefined && timeoutValidateTags > 0) {
      this.timer = setTimeout(async () => {
        this.setState({ processing: true }, this.verifyTag)
      }, timeoutValidateTags)
    } else {
      this.setState({ processing: true }, this.verifyTag)
    }
  }

  sendMixpanelEventData = async (event) => {
    const { encodingValidation } = this.state
    const mixpanelData = EncodingExtensions.getMixPanelData(this.operation, encodingValidation)
    AppStore.sendMixPanelEvent(event, mixpanelData)
  }

  verifyTag = async () => {
    const { encodingValidation } = this.state
    try {
      this.isValidate = true
      this.stopTimer()
      const encode = await EncodingExtensions.verify(encodingValidation, true, this.datadogSession?.runId)
      const productionOrderRow: ProductionOrderRow | undefined = await this.getProductionOrder(
        encode.encodingValidationResponse
      )
      const workOrderRow: WorkOrderRow | undefined = await this.getWorkOrderRow(encode.encodingValidationResponse)
      const itemAttributes = await this.getItemAttributes(encode.encodingValidationResponse)

      await this.sendMixpanelEventData('Encoding: Verify Association')
      this.setState(
        {
          encodingValidation: encode.encodingValidation,
          encodingValidationResponse: encode.encodingValidationResponse,
          processing: false,
          productionOrderRow,
          workOrderRow,
          itemAttributes
        },
        this.resetIsValidate
      )
      if (showCountersOnEncodingVerify(this.initialType, this.operation) && productionOrderRow?.id) {
        this.fetchProductionOrderRowCounter(productionOrderRow.id)
      }
    } catch (error) {
      this.isValidate = false
      this.setState({ processing: false })
      showToastError(
        (error as any).message ?? 'Unknown error during encoding validation',
        __(T.error.error),
        this.isModal
      )
    }
  }

  resetIsValidate = async () => {
    this.isValidate = false
    const scanIdentifierConfig = getScanIdentifierConfig(this.operation)
    if (!scanIdentifierConfig.rfid) {
      await this.startAntenna()
    }
  }

  stopTimer = () => {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  clearReads = async (restart: boolean = true) => {
    const { currentTagType } = this.state

    if (currentTagType !== 'ALL' && currentTagType !== this.tagTypesToCheck[0]) {
      await this.stopAntenna()
    }

    // clear received from disassociate
    this.restartAntennaAfterDisassociate = restart

    // reset replacing stoplight
    this.inReplacing = false

    // stop timer to verify
    this.stopTimer()

    //reset num kill attempt
    this.numKillAttempt = 0

    // reset kill password counter
    this.currentKillPassword = 0
    this.killPasswords = []

    // reset state
    this.setState(
      {
        encodingValidation: {
          operationId: this.operation.id,
          identifiers: []
        },
        encodingValidationResponse: undefined,
        targetTags: [],
        targetTag: [],
        processing: false,
        visibleModalTags: false,
        progress: false,
        tagGroupSelected: undefined,
        replaceTag: false,
        currentTagType: this.tagTypesToCheck[0],
        productionOrderRow: undefined,
        workOrderRow: undefined,
        counters: undefined,
        numKillAttempt: 0,
        numWriteAttempt: 0,
        itemAttributes: undefined
      },
      this.restartAntenna
    )
  }

  renderIdentifierBox = (_identifier: ConfigurationDetail, index: any) => {
    const { encodingValidation, encodingValidationResponse } = this.state
    if (encodingValidationResponse?.configuration?.details && isExpectOnlyMandatoryIdentifiers(this.operation)) {
      const identifierConfiguration = encodingValidationResponse?.configuration?.details?.find(
        (i) => i.role === _identifier.role && i.identifierType === _identifier.identifierType
      )
      if (identifierConfiguration?.optional) {
        return null
      }
    } else if (
      isExpectOnlyMandatoryIdentifiers(this.operation) &&
      EncodingExtensions.forceSimpleItemIdentifierAsOptional &&
      _identifier.identifierType === 'SIMPLE_ITEM_IDENTIFIER'
    ) {
      return null
    }
    return (
      <IdentifierBox
        widthPerc={(encodingValidation?.identifiers.length ?? 0) < 3 ? 100 : 50}
        inLine
        identifier={_identifier}
        key={index}
        onInputSubmit={(barcode) => this.onTagRead({ barcode } as any)}
        renderSimpleItemIdentifierAsNormalIdentifier
        hideCode={this.getHideIdentifierCodeByType()?.includes(_identifier.identifierType) ?? false}
        showIdentifierDetail
      />
    )
  }

  canKillTag = () => {
    const { encodingValidationResponse } = this.state
    const killTagMode = this.getEncodingKillTagMode()
    return (
      AppStore.hasAnyCapabilities('Operation.Encoding.killTag') &&
      killTagMode !== 'none' &&
      this.getTags().some((tg) => !tg.onlyforreplace) &&
      encodingValidationResponse &&
      ['NONE', 'FAIL_NONE'].includes(encodingValidationResponse.operationToPerform)
    )
  }

  canReplaceTag = () => {
    const { encodingValidationResponse } = this.state
    return (
      AppStore.hasAnyCapabilities('Operation.Encoding.replaceTag') &&
      this.getTags().length > 0 &&
      encodingValidationResponse &&
      ['NONE', 'FAIL_NONE'].includes(encodingValidationResponse.operationToPerform)
    )
  }

  canDeleteAssociation = () => {
    const { encodingValidationResponse, workOrderRow } = this.state
    return (
      AppStore.hasAnyCapabilities('Operation.Encoding.disassociate') &&
      enabledDisassociateItemTag() &&
      (encodingValidationResponse?.item?.id || (isPreEncodingWorkOrder(this.operation) && workOrderRow))
    )
  }

  enabledOperaton = () => {
    return this.canKillTag() || this.canReplaceTag() || this.canDeleteAssociation()
  }

  deleteItem = async () => {
    const { encodingValidationResponse, encodingValidation } = this.state
    const isPreEncoding = isPreEncodingWorkOrder(this.operation)
    if (
      ((isPreEncoding && encodingValidationResponse?.item) ||
        (!isPreEncoding && encodingValidationResponse?.item?.id && encodingValidationResponse?.item?.id !== null)) &&
      (await askUserConfirmation(__(T.misc.delete), __(T.confirm.confirm_delete_item_association)))
    ) {
      try {
        await this.stopAntenna()
        const payload: any = {
          operationId: this.operation.id
        }
        if (!encodingValidationResponse?.item?.id || encodingValidationResponse?.item?.id == null) {
          payload.itemIdentifierIds = encodingValidation?.identifiers
            .filter(
              (idt): idt is typeof idt & { identifierId: string } =>
                idt._status === 'CONFIRMED' && idt.identifierId !== undefined && idt.identifierId !== null
            )
            .map((idt) => idt.identifierId)
        } else {
          payload.itemId = encodingValidationResponse?.item?.id
        }
        await Encodings.disassociate(payload, this.datadogSession?.runId)
        await AppStore.decreaseDailyEncodedItems(this.operation.code)
        await this.sendMixpanelEventData('Encoding: Item Disassociated')
        showToast({ status: 'success', description: __(T.messages.tag_deleted) })

        this.clearReads((this.operation.options.itemCreationMode ?? 'async') === 'sync')
      } catch (error) {
        showToastError(error, __(T.error.error), this.isModal)
      }
    }
  }

  checKilledTag = (type: 'UHF' | 'NFC') => {
    const killTagMode = this.getEncodingKillTagMode()
    if (killTagMode === 'uhf_nfc') return true
    if (type == 'UHF' && killTagMode === 'only_uhf') return true
    if (type == 'NFC' && killTagMode === 'only_nfc') return true
    return false
  }

  getTags = (replace?: boolean) => {
    const { encodingValidation, encodingValidationResponse } = this.state
    const validTagTypes = new Set(['UHF_TAG', 'NFC_TAG', 'NFC_Tag'])
    const tagsToKill: TrmEncodingVerifyTag[] = []
    if (encodingValidation?.identifiers) {
      encodingValidation.identifiers.forEach((idt) => {
        if (validTagTypes.has(idt.identifierType) && idt.code && idt.code !== '') {
          const read = idt._added ?? idt._read ?? false
          const killable = this.checKilledTag(idt.identifierType === 'UHF_TAG' ? 'UHF' : 'NFC')
          tagsToKill.push({
            code: idt.code ?? '',
            tid: idt.identifierType === 'UHF_TAG' && idt.tid && idt.tid !== null ? idt.tid : undefined,
            type: idt.identifierType === 'UHF_TAG' ? 'UHF' : 'NFC',
            role: idt.role ?? '',
            associate:
              (idt.status === 'ASSOCIATED' || (replace && idt.status === 'REPLACED_ASSOCATION')) &&
              (read || idt._status === 'EXPECTED' || idt._errorCode === 'MISSING_TAG'),
            disassociate: false,
            read: read,
            done: false,
            progress: false,
            group: idt.group ?? '',
            itemId: idt.itemId ?? undefined,
            productionOrderRowId: idt.productionOrderRowId,
            onlyforreplace: false,
            killable: killable && read,
            killed_tag: idt._errorCode === 'KILLED_TAG',
            logicKillable: idt.logicKillable ?? false,
            tokill:
              read &&
              killable &&
              ((canKillToAssociateItem() && idt.status === 'ASSOCIATED' && idt._status !== 'EXPECTED') ||
                (idt._errorCode !== undefined &&
                  (idt._errorCode.includes('Tag Mismatch') ||
                    idt._errorCode.includes('UNKNOWN_TAG') ||
                    idt._errorCode.includes('Not Associated Tag'))))
          })
        }
      })
    }

    // check item identifier for replace
    if (encodingValidationResponse?.item?.identifiers) {
      encodingValidationResponse.item.identifiers
        .filter((t) => !['REPLACED_ASSOCIATION', 'KILLED'].includes(t.status))
        .forEach((idt) => {
          if (validTagTypes.has(idt.type)) {
            const idtType = idt.type === 'UHF_TAG' ? 'UHF' : 'NFC'
            const check = tagsToKill.some((t) => t.role === idt.role && t.type == idtType)
            if (!check) {
              tagsToKill.push({
                code: idt.code ?? '',
                tid: idt.type === 'UHF_TAG' && idt.tid && idt.tid !== null ? idt.tid : undefined,
                type: idtType,
                role: idt.role ?? '',
                associate: (canKillToAssociateItem() || replace) && idt.status === 'ASSOCIATED',
                disassociate: false,
                read: false,
                done: false,
                progress: false,
                group: idt.group ?? '',
                itemId: idt.itemId ?? undefined,
                productionOrderRowId: idt.productionOrderRowId,
                onlyforreplace: true,
                killable: false,
                killed_tag: false,
                logicKillable: false,
                tokill: false
              })
            }
          }
        })
    }
    let tagTypeOrder =
      this.tagTypesToCheck.length > 0 && this.tagTypesToCheck[0] !== 'ALL' ? this.tagTypesToCheck[0] : 'UHF'
    const killTagMode = this.getEncodingKillTagMode()
    if (killTagMode === 'only_nfc') tagTypeOrder = 'NFC'
    else if (killTagMode === 'only_uhf') tagTypeOrder = 'UHF'
    tagsToKill.sort((a) => {
      if (a.type === tagTypeOrder) return a.read ? -1 : 1
      return 1
    })
    return tagsToKill
  }

  checkKillTag = async () => {
    const killTagMode = this.getEncodingKillTagMode()
    const tags = this.getTags()
    const tagToKillable = tags.filter((i) => i.killable)
    if (tagToKillable.length === 0) {
      showToastError(
        __(T.error.can_kill_only_tag_type, {
          type: killTagMode === 'only_uhf' ? 'UHF' : 'NFC'
        }),
        __(T.error.error),
        this.isModal
      )
    } else {
      const tagToKill = tags.filter((i) => i.tokill)
      if (tagToKill.length === 0) {
        const isKilledTag = tags.filter((i) => i.killed_tag)
        showToastError(
          isKilledTag.length > 0 ? __(T.error.kill_already_killed) : __(T.error.kill_tag_not_allowed),
          __(T.error.error),
          this.isModal
        )
      } else {
        const grouped = getGroupedKillTags(tags)
        const groupedKill: TrmEncodingVerifyGroupedTag[] = []
        grouped.forEach((gr) => {
          if (gr.tags.some((tg) => !tg.onlyforreplace)) {
            groupedKill.push(gr)
          }
        })
        if (groupedKill.length === 1) {
          const find = groupedKill[0].tags.find((idt) => idt.itemId && idt.itemId !== '')
          const disassociate = find && find.associate && canKillToAssociateItem()
          const groupedTags = groupedKill[0].tags.filter((tg) => !tg.onlyforreplace)
          const message =
            groupedTags.length === 1
              ? __(disassociate ? T.confirm.confirm_disassociate_kill_tag : T.confirm.confirm_kill_tag, {
                  tag: groupedTags[0].code,
                  type: groupedTags[0].type
                })
              : __(
                  disassociate ? T.confirm.confirm_disassociate_multiple_kill_tag : T.confirm.confirm_multiple_kill_tag
                )
          if (await askUserConfirmation(__(T.misc.kill_tag), message)) {
            await this.stopAntenna()
            this.setState(
              {
                targetTag: groupedTags,
                tagGroupSelected: groupedKill[0],
                progress: true,
                visibleModalTags: true,
                targetTags: tags,
                replaceTag: false
              },
              this.checkDisassociate
            )
          }
        } else {
          this.setState({ targetTags: tags, replaceTag: false, visibleModalTags: true })
        }
      }
    }
  }

  confirmIdentifiersModal = async (target: TrmEncodingVerifyTag[]) => {
    const { replaceTag } = this.state
    await this.stopAntenna()
    if (replaceTag) {
      this.setState({ targetTag: target }, this.replaceTag)
    } else {
      this.setState({ targetTag: target }, this.checkDisassociate)
    }
  }

  closeIdentifiersModal = async (resetReplacing?: boolean) => {
    const { replaceTag } = this.state
    if (this.inReplacing && resetReplacing) {
      this.inReplacing = false
      return
    }
    if (replaceTag && this.inReplacing) return
    if (replaceTag) {
      // stop antenna
      await this.stopAntenna()

      //reset on tag read callback
      RfidReader.setOnTagReadCallback(this.onTagRead)

      // reset automatic reader stop
      RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
    }
    this.setState({ visibleModalTags: false })
  }

  // check if should do disassociate item
  checkDisassociate = async () => {
    const { targetTag, targetTags } = this.state

    // find if exist item to disassociate
    const find = targetTag.find((idt) => idt.itemId && idt.itemId !== '')

    // check if is possibile disassociate item
    if (find && find.associate && canKillToAssociateItem()) {
      try {
        // call disassociate api
        await Encodings.disassociate(
          { itemId: find.itemId, operationId: this.operation.id },
          this.datadogSession?.runId
        )
        await AppStore.decreaseDailyEncodedItems(this.operation.code)

        // find tag by code
        const indexTag = targetTags.findIndex((tg) => tg.code === find.code)

        // if tag found change status
        if (indexTag >= 0) targetTags[indexTag].disassociate = true

        // update state and call function to start kill tag process
        this.setState({ targetTags: targetTags }, this.killTag)
      } catch (error) {
        showToastError(error, __(T.error.error), this.isModal)
        this.clearReads()
      }
    } else {
      this.killTag()
    }
  }

  /* starter kill tag process */
  killTag = async () => {
    this.countTags = 0
    this.processKillTag()
  }

  processKillTag = async (resetNumKillAttempt = true) => {
    const { targetTag, targetTags } = this.state
    if (this.countTags < targetTag.length) {
      if (resetNumKillAttempt) this.numKillAttempt = 0

      // clean data dog session
      this.cleanDataDogSessionDeviceManager()

      // init date for datadog track
      if (this.datadogSession && resetNumKillAttempt) {
        this.datadogSession.startTime = new Date()
      }

      // find tag by code and type
      const indexTag = targetTags.findIndex(
        (tg) => tg.code === targetTag[this.countTags].code && tg.type === targetTag[this.countTags].type
      )

      // if tag found change status
      if (indexTag >= 0) targetTags[indexTag].progress = true

      // reset kill password counter
      this.currentKillPassword = 0
      this.killPasswords = []

      // update state and call function to kill current tag
      this.setState({ targetTags: targetTags }, this.killingSingleTag)
    } else {
      // kill completed
      showToast({ status: 'success', description: __(T.messages.tag_killed) })
      setTimeout(() => {
        this.clearReads()
      }, 1000)
    }
  }

  killingSingleTag = async () => {
    const { targetTag, targetTags } = this.state
    await sleep(AppStore.getEmulation() && !isProductionEnvironment() ? 2000 : 250)
    // kill single tag
    const ret = await this.singleKillTag()
    if (ret) {
      if (targetTag[this.countTags].tokill) {
        try {
          // call be to change tag status to KILLED
          await Encodings.kill(this.operation.id, targetTag[this.countTags] ?? undefined, this.datadogSession?.runId)
        } catch (e) {
          console.log('failure to change tag status to killed', e)
        }

        // send data to datadog
        if (this.datadogSession) {
          this.cleanDataDogSessionDeviceManager()
          const killedTag: DatadogEncodingRunKilledTag = {
            code: targetTag[this.countTags].code,
            type: targetTag[this.countTags].type
          }
          if (
            targetTag[this.countTags].type === 'UHF' &&
            targetTag[this.countTags].tid &&
            targetTag[this.countTags].tid !== ''
          )
            killedTag.tid = targetTag[this.countTags].tid

          this.datadogSession.endTime = new Date()
          this.datadogSession.killedTag = killedTag
          datadogRum.addAction('killTag', this.datadogSession)
        }
      }

      // find tag by code and type
      const indexTag = targetTags.findIndex(
        (tg) => tg.code === targetTag[this.countTags].code && tg.type === targetTag[this.countTags].type
      )

      // if tag found change status
      if (indexTag >= 0) {
        targetTags[indexTag].done = true
        targetTags[indexTag].progress = false
      }

      // increment counter for tags to kill
      this.countTags++

      //update state and recall process kill tag to continue flow
      this.setState({ targetTags: targetTags }, this.processKillTag)
    } else {
      this.killTagError()
    }
  }

  /* add a space every two characters */
  addSpaceToNfcKillCommand = (value: string) => {
    return value.replaceAll(/(.{2})/g, '$1 ').trim()
  }

  singleKillTag = async () => {
    const { targetTag } = this.state
    try {
      // get current tag
      const tag = targetTag[this.countTags] ?? undefined

      // check if tag is killable
      if (tag && tag.tokill && !tag.logicKillable) {
        let ret: any

        // call api to get password for kill nfc
        const identifiers: TagPasswordIdentifiers[] = [
          { code: tag.code, type: tag.type === 'NFC' ? 'NFC_TAG' : 'UHF_TAG' }
        ]
        const res: TagPasswordResponse[] = await Encodings.tagPasswords(this.operation.id, identifiers)
        if (res?.length === 1 && res[0].killPasswords.length > 0) {
          this.killPasswords = res[0].killPasswords
          this.currentKillPassword = 0
          this.mutualAuth = res[0].mutualAuth ?? true
          ret = await this.passwordKillTag()
        } else {
          return false
        }
        if (AppStore.getEmulation() && !isProductionEnvironment()) return true
        return ret.ok
      } else {
        return true
      }
    } catch {
      if (AppStore.getEmulation() && !isProductionEnvironment()) return true
      return false
    }
  }

  passwordKillTag = async () => {
    const killed = await this.executeKillTag()
    if (killed?.ok) {
      return killed
    } else {
      this.currentKillPassword++
      if (this.currentKillPassword < this.killPasswords.length) {
        return await this.passwordKillTag()
      } else {
        return { ok: false }
      }
    }
  }

  getCheckTagAfterKill = () => {
    return !!(this.operation.options?.checkTagAfterKill && this.operation.options.checkTagAfterKill === 'true')
  }

  executeKillTag = async () => {
    const { targetTag } = this.state

    // get current tag
    const tag = targetTag[this.countTags] ?? undefined

    try {
      let ret: any = { ok: false }

      // check if tag is killable
      if (tag?.tokill) {
        // wait 200 milliseconds
        await sleep(200)

        const killPassword = this.killPasswords[this.currentKillPassword]
        if (tag.type === 'UHF' || (tag.type === 'NFC' && !this.mutualAuth)) {
          // kill tag uhf / nfc
          ret = await RfidReader.killTag(tag.code, killPassword, tag.type, this.datadogSession, 'killTagDeviceManager')

          // se opzione abilitata si prova a leggere per verificare l'effettiva esecuzione della kill
          if (this.getCheckTagAfterKill()) {
            this.cleanDataDogSessionDeviceManager()

            // wait 200 milliseconds
            await sleep(200)

            const check = await RfidReader.readEpc(tag.code, this.datadogSession, 'readEpcDeviceManager')
            if (!check.ok) {
              // se il tag non viene letto si forza risposta positiva della kill
              return { ok: true }
            }
            return { ok: false }
          } else {
            return ret
          }
        } else {
          // get reversed uid
          const reverse = getReverseUid(tag.code)
          if (reverse.success) {
            let killed: boolean = false

            // start socket for custom commands
            await RfidReader.start({
              tagTypeToCheck: 'NFC',
              customCommandsOperationType: KILL_CUSTOM_COMMANDS_OPERATION_TYPE
            })
            await RfidReader.startWsCustomCommands(undefined, true)

            // start keep alive for socket
            RfidReader.sendWsCustomKeepAlive()

            // send login command
            const payloadLogin = {
              command:
                this.addSpaceToNfcKillCommand('BF010019000022E416') +
                ' ' +
                this.addSpaceToNfcKillCommand(reverse.reversed) +
                ' ' +
                this.addSpaceToNfcKillCommand(killPassword),
              parameters: {}
            }
            const login: any = await RfidReader.sendWsCustomCommandForKill(payloadLogin)
            this.sendDatadogCustomCommandForKill('killCustomCommandLogin', payloadLogin, login)
            if (login.success) {
              // wait 100 milliseconds
              await sleep(100)

              // send kill command
              const payloadKill = {
                command:
                  this.addSpaceToNfcKillCommand('BF010015000020B916') +
                  ' ' +
                  this.addSpaceToNfcKillCommand(reverse.reversed),
                parameters: {}
              }
              const kill: any = await RfidReader.sendWsCustomCommandForKill(payloadKill)
              this.sendDatadogCustomCommandForKill('killCustomCommand', payloadKill, kill)
              if (kill.errorDescription === 'No transponder in reader field') {
                // wait 100 milliseconds
                await sleep(100)

                // send new login command to verify if tag is correctly killed
                const loginToCheck: any = await RfidReader.sendWsCustomCommandForKill(payloadLogin)
                this.sendDatadogCustomCommandForKill('killCustomCommandLogin', payloadLogin, loginToCheck)
                if (!loginToCheck.success) {
                  killed = true
                }
              }
            }

            // stop socket come custom commands
            await RfidReader.stopWsCustomCommands(true)
            await RfidReader.stop({ customCommandsOperationType: KILL_CUSTOM_COMMANDS_OPERATION_TYPE })

            // return result
            ret = { ok: killed }

            return ret
          } else {
            return { ok: false }
          }
        }
      }
    } catch {
      if (AppStore.getEmulation() && !isProductionEnvironment()) return { ok: true }
      return { ok: false }
    }
  }

  sendDatadogCustomCommandForKill = (action: string, payload: any, response: any) => {
    this.cleanDataDogSessionDeviceManager()
    if (this.datadogSession) {
      const payloadDataDog: DatadogEncodingRunDeviceManager = {
        url: RfidReader.getWsCustomCommandsUrl(undefined, true),
        payload: payload,
        response: response
      }
      this.datadogSession.deviceManager = payloadDataDog
      datadogRum.addAction(action, this.datadogSession)
    }
  }

  /* retry kill when there is an error */
  killTagError = async () => {
    if (this.numKillAttempt === MAX_KILL_TAG_ATTEMPT) {
      // request confirm to user for kill retry
      const ok = await askUserConfirmation(__(T.misc.kill_tag), __(T.misc.retry_kill_tag))
      if (ok) {
        // reset kill attempt
        this.numKillAttempt = 1

        // reset kill password current counter
        this.currentKillPassword = 0

        // call kill tag
        this.processKillTag(false)
      } else {
        // stop the process
        showToastError(__(T.error.kill_tag_not_success), __(T.error.error), this.isModal)

        // reset
        this.clearReads()
      }
    } else {
      // reset kill password current counter
      this.currentKillPassword = 0

      // increment kill attempt
      this.numKillAttempt++
      // call kill tag
      this.processKillTag(false)
    }
  }

  checkReplaceTag = () => {
    this.setState({ replaceTagLoading: true }, this.checkTagReplacing)
  }

  checkTagReplacing = async () => {
    const { encodingValidation } = this.state
    try {
      await EncodingExtensions.onReplaceTagPressedExtension(encodingValidation)
      this.continueReplaceTag()
    } catch (e) {
      showToastError(e, __(T.error.error), this.isModal)
    } finally {
      this.setState({ replaceTagLoading: false })
    }
  }

  continueReplaceTag = () => {
    // get all tags
    const tags = this.getTags(true)

    // check if exist tags associated
    const tagToKill = tags.filter((i) => i.associate)
    if (tagToKill.length === 0) {
      showToastError(__(T.error.replace_tag_not_allowed), __(T.error.error), this.isModal)
    } else {
      const grouped = getGroupedKillTags(tags, true)

      const find = grouped.filter((gr) => !gr.tags[0].read)

      let groupSelected = undefined
      if (find.length === 1) {
        groupSelected = find[0]
      }

      // open modal to start replaceTag flow
      this.setState({ targetTags: tags, visibleModalTags: true, replaceTag: true, tagGroupSelected: groupSelected })
    }
  }

  replaceTag = () => {
    this.inReplacing = false
    this.countTags = 0
    const { targetTag, targetTags } = this.state
    // find tag for type and code
    const indexTag = targetTags.findIndex(
      (tg) => tg.code === targetTag[this.countTags].code && tg.type === targetTag[this.countTags].type
    )
    if (indexTag >= 0) {
      // clean data dog session
      this.cleanDataDogSessionDeviceManager()

      // init date for datadog track
      if (this.datadogSession) {
        this.datadogSession.startTime = new Date()
      }

      //change tag status
      targetTags[indexTag].progress = true
      targetTags[indexTag].towrite =
        targetTag[this.countTags].type === 'UHF' && getEnableUhfWrite(this.operation) !== 'no'

      // update state and call callback for start antenna
      this.setState({ targetTags: targetTags }, this.startAntennaReplaceTag)
    } else {
      this.replaceTagError()
    }
  }

  /* start antenna for replace tag flow */
  startAntennaReplaceTag = async () => {
    const { targetTag, encodingValidation } = this.state

    /* remove not associated tags from reader */
    const notAssociated = encodingValidation.identifiers.filter(
      (idt) => idt._errorCode === 'Not Associated Tag' && ['UHF_TAG', 'NFC_TAG', 'NFC_Tag'].includes(idt.identifierType)
    )
    const toRemove: string[] = []
    notAssociated.forEach((c) => {
      toRemove.push(c.code)
    })
    RfidReader.removeTags(toRemove)

    //set custom tag read callback
    RfidReader.setOnTagReadCallback(this.onReplaceTagRead)

    //remove automatic stop
    RfidReader.setAutomaticStop(false)

    // start antenna with targetTag[this.countTags].type param to start only antenna for replaced tag type
    await this.startAntenna(targetTag[this.countTags].type)
  }

  /* on tag read callback for replace tag flow */
  onReplaceTagRead = async (tag: TmrTag) => {
    const { targetTag, targetTags } = this.state

    // if replacing in progress discard all received tags
    if (this.inReplacing) {
      RfidReader.removeTags([tag.uid ?? tag.epc])
      return
    }
    this.inReplacing = true

    // if an NFC tag arrives during the replace UHF phase, remove the tag from the reader
    if (targetTag[this.countTags].type === 'UHF' && !!tag.uid) {
      RfidReader.removeTags([tag.uid])
      return
    }

    // if an UHF tag arrives during the replace NFC phase, remove the tag from the reader
    if (targetTag[this.countTags].type === 'NFC' && !!tag.epc) {
      RfidReader.removeTags([tag.epc])
      return
    }

    //added a sleep to destroy correctly the timers or wait isValidate flag
    await sleep(Math.random() * 500)

    // stop antenna
    await this.stopAntenna()

    // define identifier type
    const identifierType = tag.uid ? 'NFC' : 'UHF'
    if (targetTag[this.countTags].type === identifierType) {
      // find tag for type and code
      const indexTag = targetTags.findIndex(
        (tg) => tg.code === targetTag[this.countTags].code && tg.type === targetTag[this.countTags].type
      )
      if (indexTag >= 0) {
        if (identifierType === 'UHF' && getEnableUhfWrite(this.operation) !== 'no') {
          targetTags[indexTag].done = true
          targetTags[indexTag].progress = false
          targetTags[indexTag].writing = true
          targetTags[indexTag].replacingCode = tag.uid ?? tag.epc
          targetTags[indexTag].replacingTid = tag.tid ?? ''

          this.setState({ targetTags: targetTags, numWriteAttempt: 0 }, this.computeTags)
        } else {
          // change tag status
          targetTags[indexTag].done = true
          targetTags[indexTag].progress = false
          targetTags[indexTag].replacing = true
          targetTags[indexTag].replacingCode = tag.uid ?? tag.epc
          targetTags[indexTag].replacingTid = tag.tid ?? ''

          this.setState({ targetTags: targetTags }, this.singleReplaceTag)
        }
      } else {
        this.replaceTagError()
      }
    }
  }

  /* compute tags */
  computeTags = async () => {
    const { numWriteAttempt, encodingValidationResponse, targetTag, targetTags } = this.state
    try {
      const target = targetTag[this.countTags] ?? undefined
      if (target) {
        const res = await EncodingExtensions.computeTags(
          this.operation,
          [
            {
              code: target.replacingCode,
              role: target.role,
              group: target.group,
              tid: target.replacingTid,
              identifierType: 'UHF_TAG'
            }
          ],
          AppStore.defaultWorkstation.id,
          '',
          encodingValidationResponse?.item?.product ? encodingValidationResponse.item.product.code : '',
          undefined,
          undefined,
          this.datadogSession?.runId
        )
        const desiderCode = getDesiredCode(res)
        if (desiderCode === '') {
          // errore
          this.replaceTagError({ message: __(T.error.compute_tags_failed) })
        } else {
          //si scrive il nuovo tag con la write del reader
          const indexTag = targetTags.findIndex(
            (tg) => tg.code === targetTag[this.countTags].code && tg.type === targetTag[this.countTags].type
          )
          if (indexTag >= 0) {
            targetTags[indexTag].desiderCode = desiderCode

            this.writeTagStartTime = undefined
            this.setState(
              { targetTags: targetTags, numWriteAttempt: numWriteAttempt + 1, writeRetryCount: 0 },
              this.writeTag
            )
          } else {
            this.replaceTagError()
          }
        }
      } else {
        this.replaceTagError()
      }
    } catch (error) {
      this.replaceTagError(error)
    }
  }

  executeReadEpcWithMetrics = async (requestedEpc: string) => {
    const result = await RfidReader.readEpc(requestedEpc, this.datadogSession, 'readEpcDeviceManager')
    this.trackReadEpcMismatch(requestedEpc, result.value)
    return result
  }

  /* verifica l'effettiva scrittura rileggendo il tag, con un secondo tentativo dopo 200 millisecondi */
  checkWrittenTag = async (writtenEpc: string) => {
    this.cleanDataDogSessionDeviceManager()

    let check = await this.executeReadEpcWithMetrics(writtenEpc)
    if (check.ok) return true

    // wait 200 milliseconds
    await sleep(200)

    // second check to verify writing
    check = await this.executeReadEpcWithMetrics(writtenEpc)
    return check.ok
  }

  /* write tag */
  writeTag = async () => {
    const { targetTag, targetTags, writeRetryCount } = this.state

    if (!this.writeTagStartTime) {
      this.writeTagStartTime = new Date()
    }

    try {
      const target = targetTag[this.countTags] ?? undefined
      if (target) {
        const targetTid = target.replacingTid ?? ''

        // define use tid to write
        let useTidToWrite = !!(getUseTidTargetDuringWrite(this.operation) && targetTid && targetTid !== '')

        // get antenna uhf
        const antennaUHF = RfidReader.getAntenna()

        // deactivate write with tid for device caen-slate
        if (antennaUHF?.reader?.deviceType === 'caen-slate') useTidToWrite = false

        const writePasswords: string[] = getEncodingTagPassword(this.operation)
        const writeTarget = useTidToWrite ? targetTid : target.replacingCode
        const datadogWriteInfo = {
          action: 'writeEpcDeviceManager',
          writeRetryCount: writeRetryCount,
          writeTagStartTime: this.writeTagStartTime
        }

        let ret: any = await RfidReader.writeEpc(
          writeTarget,
          target.desiderCode,
          writePasswords,
          useTidToWrite,
          this.datadogSession,
          datadogWriteInfo
        )

        // se opzione abilitata, al fallimento della scrittura si prova a lockare il tag e a riscrivere
        if (!ret.ok && getLockTagOnWriteFailure(this.operation)) {
          const lock: any = await RfidReader.lockTag(
            writeTarget,
            target.desiderCode,
            writePasswords,
            useTidToWrite,
            this.datadogSession
          )
          if (lock.ok) {
            ret = await RfidReader.writeEpc(
              writeTarget,
              target.desiderCode,
              writePasswords,
              useTidToWrite,
              this.datadogSession,
              datadogWriteInfo
            )
          }
        }

        if (ret.ok) {
          // se opzione abilitata si prova a leggere per verificare l'effettiva scrittura
          if (getCheckTagAfterWrite(this.operation) && !(await this.checkWrittenTag(target.desiderCode))) {
            this.setState({ numWriteAttempt: MAX_WRITE_ATTEMPT }, this.writeTagError)
            return
          }

          const indexTag = targetTags.findIndex(
            (tg) => tg.code === targetTag[this.countTags].code && tg.type === targetTag[this.countTags].type
          )
          if (indexTag >= 0) {
            // change tag status
            targetTags[indexTag].writing = false
            targetTags[indexTag].writed = true
            targetTags[indexTag].replacing = true

            this.writeTagStartTime = undefined

            this.setState({ targetTags: targetTags, writeRetryCount: 0 }, this.singleReplaceTag)
          } else {
            this.replaceTagError()
          }
        } else {
          this.writeTagError()
        }
      } else {
        this.replaceTagError()
      }
    } catch {
      this.writeTagError()
    }
  }

  /* write tag error */
  writeTagError = async () => {
    const { numWriteAttempt } = this.state
    if (numWriteAttempt === MAX_WRITE_ATTEMPT) {
      const ok = await askUserConfirmation(__(T.misc.write_tag), __(T.misc.retry_write_tag))
      if (ok) {
        this.setState(
          (prevState) => ({
            numWriteAttempt: 1,
            writeRetryCount: prevState.writeRetryCount + 1
          }),
          this.writeTag
        )
      } else {
        this.replaceTagError({ message: __(T.error.write_tag) })
      }
    } else {
      this.setState(
        (prevState) => ({
          numWriteAttempt: numWriteAttempt + 1,
          writeRetryCount: prevState.writeRetryCount + 1
        }),
        this.writeTag
      )
    }
  }

  singleReplaceTag = async () => {
    const { targetTag, targetTags } = this.state
    try {
      const target = targetTag[this.countTags] ?? undefined
      if (target && !!target.code && !!target.replacingCode) {
        // get old tag
        const oldTag: TrmEncodingVerifyReplaceTag = { code: target.code }
        if (target.type === 'UHF' && target.tid && target.tid !== '') oldTag.tid = target.tid

        // get new tag
        const newTag: TrmEncodingVerifyReplaceTag = { code: target.replacingCode }
        if (target.type === 'UHF' && target.replacingTid && target.replacingTid !== '') newTag.tid = target.replacingTid
        if (getEnableUhfWrite(this.operation) !== 'no' && !!target.desiderCode) {
          newTag.desideredCode = target.desiderCode
        }

        if (getEnableUhfWrite(this.operation) !== 'no' && !!target.desiderCode) {
          newTag.desideredCode = target.desiderCode
        }
        // call replace tag
        await Encodings.replaceTag(
          this.operation.id,
          oldTag,
          newTag,
          AppStore.defaultWorkstation.id,
          this.datadogSession?.runId
        )

        // send data to datadog
        if (this.datadogSession) {
          this.cleanDataDogSessionDeviceManager()
          const replacedTag: DatadogEncodingRunReplaceTag = {
            old: { code: target.code },
            new: { code: target.replacingCode }
          }
          if (target.type === 'UHF' && target.tid && target.tid !== '') replacedTag.old.tid = target.tid
          if (target.type === 'UHF' && target.replacingTid && target.replacingTid !== '')
            replacedTag.new.tid = target.replacingTid
          if (getEnableUhfWrite(this.operation) !== 'no' && !!target.desiderCode) {
            replacedTag.new.desideredCode = target.desiderCode
          }
          this.datadogSession.replaceTag = replacedTag
          this.datadogSession.endTime = new Date()
          datadogRum.addAction('replaceTag', this.datadogSession)
        }

        // change tag status
        target.replacing = false
        target.replaced = true

        // update state and call success callback
        this.setState({ targetTags: targetTags, writeRetryCount: 0 }, this.successReplaceTag)
      } else {
        this.replaceTagError()
      }
    } catch (error) {
      this.replaceTagError(error)
    }
  }

  /* replace tag error */
  replaceTagError = async (error?: any) => {
    this.writeTagStartTime = undefined
    this.setState({ writeRetryCount: 0 })
    showToastError(
      error?.message ? error.message : __(T.error.replace_tag_not_success),
      __(T.error.error),
      this.isModal
    )
    this.clearReads()
  }

  /* replace tag success */
  successReplaceTag = () => {
    showToast({ status: 'success', description: __(T.messages.tag_replaced) })
    setTimeout(() => {
      this.clearReads()
    }, 1000)
  }

  /* change tab callback */
  onTabClick = (tab) => {
    if (this.operation.options.showOnlyVerify === 'true') {
      return
    }
    if (tab === 'associate') {
      // update state and call callback to change tab
      this.setState({ processing: true }, this._changeTab)
    }
  }

  /* change tab function */
  _changeTab = async () => {
    const { originTemplatePath } = this.state
    // clear tag read
    RfidReader.clear()

    // stop antennas
    await this.stopAntenna()

    // change tab with timeout
    setTimeout(() => {
      const pathPrefix = originTemplatePath ? `${originTemplatePath}/` : ''
      Router.navigate(`/encoding/${pathPrefix}:opCode` as any, {
        opCode: this.operation.code
      })
    }, this.operation.options.antennaTurnWaitingInterval ?? 1000)
  }

  getProductionOrder = async (
    validationResponse: EncodingValidationResponse
  ): Promise<ProductionOrderRow | undefined> => {
    const item = validationResponse?.item
    if (!isPreEncodingWorkOrder(this.operation)) {
      if (!item?.id) return undefined
      if (item.productionOrderRow) return item.productionOrderRow
      if (!item.productionOrderRowId) return undefined

      try {
        return await EncodingProductionOrderRows.get(item.productionOrderRowId)
      } catch {
        return undefined
      }
    }

    const identifiers = item?.identifiers ?? []
    if (!identifiers.length) return undefined

    const productionOrderRowIds = identifiers.map((id) => id.productionOrderRowId ?? item?.productionOrderRowId)
    if (!productionOrderRowIds.every((id) => !!id)) return undefined

    const uniqueProductionOrderRowIds = [...new Set(productionOrderRowIds)]
    if (uniqueProductionOrderRowIds.length !== 1) return undefined

    try {
      return await EncodingProductionOrderRows.get(uniqueProductionOrderRowIds[0]!)
    } catch {
      return undefined
    }
  }

  getWorkOrderRow = async (validationResponse: EncodingValidationResponse): Promise<WorkOrderRow | undefined> => {
    if (!isPreEncodingWorkOrder(this.operation)) {
      return undefined
    }

    const identifiers = validationResponse?.item?.identifiers ?? []
    if (!identifiers.length) return undefined

    const workOrderRowIds = identifiers
      .map((id) => id.workOrderRowId)
      .filter((id): id is string => id !== undefined && id !== null)
    if (workOrderRowIds.length !== identifiers.length) return undefined

    const uniqueWorkOrderRowIds = [...new Set(workOrderRowIds)]
    if (uniqueWorkOrderRowIds.length !== 1) return undefined

    try {
      return await WorkOrderRows.get(uniqueWorkOrderRowIds[0])
    } catch {
      return undefined
    }
  }

  /* only one and only one valid item is retrieved: item.id is set exclusively in that case */
  getItemAttributes = async (
    validationResponse: EncodingValidationResponse
  ): Promise<TmrSimpleAttributesMap[] | undefined> => {
    if (!getShowItemAttributesBox(this.operation) || !validationResponse?.item?.id) {
      return undefined
    }
    try {
      return await AttributeMappings.getItemAttribute(validationResponse.item)
    } catch {
      return undefined
    }
  }

  restartAntenna = async () => {
    const { currentTagType } = this.state

    //reset on tag read callback
    RfidReader.setOnTagReadCallback(this.onTagRead)

    // reset automatic reader stop
    RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)

    //reset validate stoplight
    this.isValidate = false

    //clear all tags
    RfidReader.clear()

    //if not reading start all antennas
    if (!RfidReader.isReading() && this.restartAntennaAfterDisassociate) {
      await RfidReader.start({
        restart: getAutomaticRestartAntenna(this.operation),
        tagTypeToCheck: currentTagType,
        customCommandsOperationType: this.getAntennaCustomCommands()
      })
    }
    this.restartAntennaAfterDisassociate = true
  }

  startAntenna = async (tagTypeInput?: OperationEncodingTagType) => {
    const { currentTagType } = this.state
    let tagType: OperationEncodingTagType = currentTagType
    if (isValidOperationEncodingTagType(tagTypeInput)) {
      tagType = tagTypeInput
    }

    if (!RfidReader.isReading()) {
      this.setState({ starting: true })
      await RfidReader.start({
        restart: getAutomaticRestartAntenna(this.operation),
        tagTypeToCheck: tagType,
        customCommandsOperationType: this.getAntennaCustomCommands()
      })
    }
  }

  /* stop antenna if reading */
  stopAntenna = async () => {
    if (RfidReader.isReading()) {
      this.setState({ starting: true })
      await RfidReader.stop({ customCommandsOperationType: this.getAntennaCustomCommands() })
      return true
    }
    return false
  }

  fetchProductionOrderRowCounter = async (productionOrderRowId: string) => {
    try {
      const countersResponse = await Encodings.getProductionOrderRowCounter(productionOrderRowId)
      this.setState({ counters: countersResponse?.data })
    } catch (error) {
      showToastError(error, __(T.error.error), this.isModal)
    }
  }

  getHideIdentifierCodeByType = (): IdentifierType[] | undefined => {
    return undefined
  }

  getOrderData = () => {
    const { productionOrderRow, workOrderRow } = this.state
    return productionOrderRow?.productionOrder ?? workOrderRow?.workOrder
  }

  getProductData = () => {
    const { encodingValidationResponse, productionOrderRow, workOrderRow } = this.state
    if (!isPreEncodingWorkOrder(this.operation)) {
      return encodingValidationResponse?.item?.product
    }
    return productionOrderRow?.product ?? workOrderRow?.product ?? encodingValidationResponse?.item?.product
  }

  /* render */
  render() {
    const {
      encodingValidation,
      encodingValidationResponse,
      antennaOn,
      processing,
      starting,
      options,
      targetTags,
      visibleModalTags,
      currentTagType,
      progress,
      tagGroupSelected,
      replaceTag,
      productionOrderRow,
      workOrderRow,
      counters,
      replaceTagLoading,
      itemAttributes
    } = this.state

    const identifiers = encodingValidation?.identifiers ?? []
    const scanIdentifierConfig = getScanIdentifierConfig(this.operation)
    const pageLayout = getEncodingPageLayout()
    if (currentTagType !== 'ALL') {
      const identifierAlreadyRead = identifiers.find(
        (i) =>
          (i.identifierType === 'NFC_TAG' && currentTagType === 'UHF') ||
          (i.identifierType === 'UHF_TAG' && currentTagType === 'NFC')
      )
      const identifierToModify = identifiers.some(
        (i) =>
          (i.identifierType === 'NFC_TAG' && currentTagType === 'NFC') ||
          (i.identifierType === 'UHF_TAG' && currentTagType === 'UHF')
      )
      if (
        identifierAlreadyRead?._status === 'ERROR' &&
        this.operation.options?.startAntennaOrder === 'nfc_uhf' &&
        currentTagType === 'UHF' &&
        !identifierToModify
      ) {
        identifiers.push({
          identifierType: 'UHF_TAG',
          _status: 'TO_BE_READ'
        })
      } else if (
        identifierAlreadyRead?._status === 'ERROR' &&
        this.operation.options?.startAntennaOrder === 'uhf_nfc' &&
        currentTagType === 'NFC' &&
        !identifierToModify
      ) {
        identifiers.push({
          identifierType: 'NFC_TAG',
          _status: 'TO_BE_READ'
        })
      }
    }
    const identifiersToShow = identifiers.filter((i) => !i._hidden)
    const order = this.getOrderData()
    const product = this.getProductData()
    const orderDetails = productionOrderRow
      ? EncodingExtensions.getProductionOrderDetails({
          productionOrderRow,
          encodingValidationResponse
        })
      : EncodingExtensions.getWorkOrderDetails({ workOrderRow, encodingValidationResponse })

    return (
      <Page
        headerRight={
          <EncodingRightHeader
            onTabClick={this.onTabClick}
            antennaOn={antennaOn}
            antennaTagType={currentTagType}
            operation={this.operation}
            options={options}
          />
        }
        title={this.operation.description}
        enableEmulation
        activeAntennaSettings
      >
        {identifiersToShow.length > 0 && (
          <Page.Sidebar
            width={pageLayout.sideBarWidth}
            pt={pageLayout.sideBarPadding?.t}
            pb={pageLayout.sideBarPadding?.b}
            pl={pageLayout.sideBarPadding?.l}
          >
            <Box flex>
              {((!!counters && !!order && !!product) || !!order) && (
                <Box mb={10}>
                  {!!counters && !!order && !!product && (
                    <Box>
                      <EncodingCountersBox counters={counters} />
                    </Box>
                  )}
                  {!!order && (
                    <Box mb={10}>
                      <InfoBox
                        title={productionOrderRow ? __(T.misc.production_order) : __(T.misc.work_order)}
                        subtitle={orderDetails.subtitle}
                        data={orderDetails.data}
                        fields={productionOrderRow ? EncodingExtensions.getProductionOrderFields() : []}
                      />
                    </Box>
                  )}
                </Box>
              )}
              {!!product && (
                <ProductInfoBox
                  paddingCard={pageLayout.productBoxPaddingCard}
                  product={product}
                  fields={getEncodingProductFields()}
                  productTitleStyle={pageLayout.productTitleStyle}
                />
              )}
              {getShowItemAttributesBox(this.operation) && !!itemAttributes?.length && (
                <Box mt={10}>
                  <ItemAttributesCard attributes={itemAttributes} title={__(T.misc.item_info)} />
                </Box>
              )}
              <Box flex />
              {identifiersToShow.length > 0 && (
                <Button
                  variant="default"
                  disabled={starting}
                  title={__(T.misc.clear)}
                  onClick={() => this.clearReads()}
                />
              )}
            </Box>
          </Page.Sidebar>
        )}
        <Page.Content notBoxed>
          {identifiersToShow.length > 0 && (
            <>
              <Box flex row justify={'space-between'} style={{ flexWrap: 'wrap', paddingBottom: 2 }}>
                {identifiersToShow.map((element, index) => this.renderIdentifierBox(element, index))}
              </Box>
              {this.enabledOperaton() && (
                <Box row>
                  {this.canKillTag() && (
                    <Button flex variant="secondary" title={__(T.misc.kill_tag)} onClick={this.checkKillTag} />
                  )}
                  {this.canReplaceTag() && (
                    <>
                      <Spacer />
                      <Button
                        loading={replaceTagLoading}
                        flex
                        variant="secondary"
                        title={__(T.misc.replace_tag)}
                        onClick={this.checkReplaceTag}
                      />
                    </>
                  )}
                  {this.canDeleteAssociation() && (
                    <>
                      <Spacer />
                      <Button flex title={__(T.misc.delete_association)} onClick={this.deleteItem} />
                    </>
                  )}
                </Box>
              )}
            </>
          )}
          {identifiersToShow.length === 0 && (
            <EncodingReading
              starting={starting}
              startAntenna={this.startAntenna}
              antennaOn={antennaOn}
              antennaTagType={currentTagType}
              message={
                antennaOn ? __(T.messages.bring_an_item_to_antenna) : __(T.messages.connection_to_station_in_progress)
              }
              disableAntenna={!scanIdentifierConfig.rfid}
              identifier={
                scanIdentifierConfig.serial
                  ? {
                      identifierType: 'SIMPLE_ITEM_IDENTIFIER',
                      _status: 'TO_BE_READ'
                    }
                  : undefined
              }
              onInputSubmit={scanIdentifierConfig.serial ? (barcode) => this.onTagRead({ barcode } as any) : undefined}
            />
          )}
        </Page.Content>
        {processing && <FullLoadingLayer message={this.getProcessingMessage()} />}
        {visibleModalTags && (
          <IdentifierListModal
            visible
            tags={targetTags}
            onClose={this.closeIdentifiersModal}
            onConfirm={this.confirmIdentifiersModal}
            progress={progress}
            selected={tagGroupSelected}
            replace={replaceTag}
          />
        )}
      </Page>
    )
  }
}
